import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import { ArrowRight } from "lucide-react";

export const StatCounter = ({ value, suffix = "", label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const dur = 1600;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) requestAnimationFrame(tick);
      else setN(value);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <div ref={ref} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="text-center">
      <div className="font-display text-4xl md:text-6xl font-bold text-[#0A221C]">
        {n.toLocaleString("en-IN")}
        <span className="text-[#4ef72d]">{suffix}</span>
      </div>
      <p className="text-[#4A5D58] text-sm md:text-base mt-2">{label}</p>
    </div>
  );
};

export const CTABand = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're in! Watch out for our best travel deals.");
      setEmail("");
    } catch {
      toast.error("Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="ll-container">
      <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0A221C] px-8 py-16 md:p-20 text-center">
        <div className="grain" />
        <div className="relative max-w-2xl mx-auto">
          <p className="eyebrow text-[#4ef72d]">Never miss an adventure</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-4">
            Handpicked journeys, straight to your inbox
          </h2>
          <p className="text-white/70 mt-4">
            Join our travellers&apos; circle for seasonal offers, new itineraries and insider tips.
          </p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 mt-8 max-w-md mx-auto">
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-6 py-3.5 text-white placeholder:text-white/50 focus:outline-none focus:border-[#4ef72d]"
            />
            <button data-testid="newsletter-submit" disabled={loading} className="btn-primary disabled:opacity-60">
              {loading ? "..." : "Subscribe"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative h-[52vh] min-h-[420px] flex items-end overflow-hidden">
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A221C] via-[#0A221C]/40 to-[#0A221C]/20" />
    <div className="ll-container relative pb-14">
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow text-[#4ef72d]">
        {eyebrow}
      </motion.p>
      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="font-display text-5xl md:text-7xl font-bold text-white mt-3 max-w-4xl leading-none">
        {title}
      </motion.h1>
      {subtitle && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }} className="text-white/80 text-lg mt-4 max-w-2xl">
          {subtitle}
        </motion.p>
      )}
    </div>
  </section>
);

export const SectionEnquiryLinks = () => (
  <div className="flex flex-wrap gap-3 mt-8">
    <Link to="/domestic-tours" className="btn-outline">Domestic Tours</Link>
    <Link to="/international-tours" className="btn-outline">International Tours</Link>
  </div>
);
